import React, { Component, Fragment } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Button } from 'components/button';
import { Alert, Loading } from 'components/cosmos';
import { Form } from 'components/form';
import { Select } from 'components/input';
import { InputGroup } from 'components/input/group';
import { Toggle } from 'components/toggle';
import InfoTooltip from 'components/tooltip/InfoTooltip';
import { I18n, Polyglot } from 'components/translate';
import { showSurvey, SURVEY_ID_LIST } from 'configs/Survicate';
import { withShellContext } from 'hoc/withShellContext';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import ConfigCertificateEditor from 'modules/configManager/configCertificate/ConfigCertificateEditor';
import { ContentBasePage } from 'modules/content';
import { FeatureToggles } from 'modules/featureToggle';
import { UiService } from 'modules/ui';
import { PropTypes } from 'prop-types';

import NoCertificateMobileMembership from './assets/no-certificate-mobile-membership.svg';
import { sendCertificateEnabledEvent } from './ConfigCertificateEvent';

import { getMergedTags } from '../../../components/canvasEditor/helpers/getMergedTags';
import ConfigService from '../ConfigService';

import './ConfigCertificate.scss';

@withShellContext
@inject((stores) => ({
  isMobile: stores.UiStore.isMobile,
  storeCertificate: stores.ConfigStore.certificate,
  isQrCodeEnabled: stores.FeatureToggleStore.featureToggles.get(
    FeatureToggles.CertificateQrCode
  )
}))
@observer
export default class ConfigCertificate extends Component {
  static propTypes = {
    storeCertificate: MobxPropTypes.objectOrObservableObject,
    tabIndex: PropTypes.number,
    isMobile: PropTypes.bool,
    isMembership: PropTypes.bool,
    isQrCodeEnabled: PropTypes.bool
  };

  state = {
    showLoader: true,
    selectedCertificate: null
  };

  newCertificateInput = React.createRef();

  componentDidMount() {
    this.getData();
  }

  selectInitialCertificate() {
    const { certificates } = this.props.storeCertificate;
    const hasCertificates = certificates && certificates.length > 0;
    const selectedCertificate = hasCertificates ? certificates[0] : null;

    this.setState({ selectedCertificate });
  }

  async getData() {
    this.addLoader();

    await this.getCertificateData();

    this.selectInitialCertificate();

    this.removeLoader();
  }

  async getCertificateData() {
    await ConfigService.fetchCertificateData();
  }

  removeLoader() {
    this.setState({ showLoader: false });
  }

  addLoader() {
    this.setState({ showLoader: true });
  }

  isMergedTag({ text = '' }) {
    return text.startsWith('*|') && text.endsWith('|*');
  }

  findMergedTag(item) {
    const { isQrCodeEnabled } = this.props;

    const translatedItem = getMergedTags(isQrCodeEnabled).find((mergedTag) => {
      const translatedMergedTag = Polyglot.t(`certificate.${mergedTag.key}`);

      return `*|${translatedMergedTag}|*` === item.text;
    });

    return translatedItem;
  }

  translateMergedTag(item) {
    const translatedItem = this.findMergedTag(item);

    if (translatedItem) {
      item.mergedTag = `*|${translatedItem.key}|*`;
    }
  }

  setMergedTags(certificate) {
    certificate.templateJson.objects.forEach((item) => {
      if (this.isMergedTag(item)) {
        this.translateMergedTag(item);
      }
    });
  }

  refreshCanvas() {
    const { canvas } = this.props.storeCertificate;

    if (!canvas) {
      return;
    }

    const { selectedCertificate } = this.state;

    selectedCertificate.templateJson = canvas.toJSON();
    this.setMergedTags(selectedCertificate);
    selectedCertificate.templateSvg = canvas.toSVG();
  }

  unsetHasChangeOnTemplate = () =>
    ConfigService.setCertificateData({ hasChangeOnTemplate: false });

  saveHandler = async () => {
    this.addLoader();
    this.refreshCanvas();

    await ConfigService.updateCertificateData();

    this.unsetHasChangeOnTemplate();
    this.getData();
    this.removeLoader();

    showSurvey(SURVEY_ID_LIST.CERTIFICATE);
  };

  setTemplate = (selectedCertificate) => {
    this.setState({ selectedCertificate });
  };

  openCertificate = async () => {
    const popup = UiService.openNewTabWithLoading();
    const { selectedCertificate } = this.state;

    this.refreshCanvas();

    const canvas = await ConfigService.createCanvas(
      selectedCertificate.templateJson
    );

    if (canvas && selectedCertificate?.templateJson) {
      try {
        const data = await ConfigService.getCertificatePreviewLink(canvas);

        if (popup) {
          popup.location.href = data.pdfLink;
        } else {
          UiService.setAlertWarning('config.certificate.popup_blocked');
        }
      } catch {
        UiService.setAlertError('unexpected_error');
        popup?.close();
      }
    }
  };

  addCertificate = async () => {
    const input = this.newCertificateInput?.current;

    if (input && input.value) {
      this.addLoader();

      let name = input.value;
      const { certificates } = this.props.storeCertificate;
      const notUnique = certificates.find((item) => item.name === name);

      if (notUnique) {
        name += '(1)';
      }

      const newCertificate = await ConfigService.createCertificate(name);

      certificates.push(newCertificate);
      ConfigService.setCertificateData(certificates);

      this.setState({ selectedCertificate: newCertificate });

      this.removeLoader();
    }
  };

  toogleAllowProgress = ({ target: { value } }) => {
    ConfigService.setCertificateData({ allowProgressDisplay: value });
  };

  toogleEnableCertificate = ({ target: { value } }) => {
    const { selectedCertificate } = this.state;

    if (selectedCertificate) {
      const { certificates } = this.props.storeCertificate;
      const editingCertificate = certificates.find(
        (item) => item.value === selectedCertificate.value
      );

      if (editingCertificate) {
        editingCertificate.enableStudentCertificate = value;
        ConfigService.setCertificateData({ certificates });
      }
    }

    sendCertificateEnabledEvent(value);
  };

  renderConfigurationForm() {
    const { isMembership } = this.props;
    const { allowProgressDisplay } = this.props.storeCertificate;
    const { selectedCertificate } = this.state;

    return (
      <div className="configcertificate _mt-9">
        <h2 className="_font-weight-light _text-4 _text-gray-900 _mb-6">
          <I18n label="general.configs" />
        </h2>

        {!isMembership && (
          <div className="configcertificate__content">
            <div className="custom__options">
              <label className="_font-weight-light _text-3 _text-gray-900 _mb-1 _mt-0">
                <I18n label="general.progress" />
              </label>
              <label className="_m-0 _text-gray-600 description-label">
                <I18n label="config_manager.config_certicate.allow_progress" />
              </label>
            </div>
            <Toggle
              id="allowProgressDisplay"
              key="input__toggle__label"
              name="allowProgressDisplay"
              className="configcertificate__toogle"
              checked={allowProgressDisplay}
              onChange={this.toogleAllowProgress}
            />
          </div>
        )}

        <div className="configcertificate__content _mt-4">
          <div className="custom__options">
            <label className="_font-weight-light _text-3 _text-gray-900 _mb-1 _mt-0">
              <I18n label="general.publication" />
            </label>
            <label className="_m-0 _text-gray-600 description-label">
              <I18n label="config_manager.config_certicate.enable_certificate" />
            </label>
          </div>
          <Toggle
            id="enableStudentCertificate"
            key="enableStudentCertificate"
            name="enableStudentCertificate"
            className="configcertificate__toogle"
            checked={
              selectedCertificate &&
              selectedCertificate.enableStudentCertificate
            }
            onChange={this.toogleEnableCertificate}
            disabled={!allowProgressDisplay}
          />
        </div>
      </div>
    );
  }

  renderMobilePreview() {
    return (
      <div className="configcertificate__preview-mobile">
        <div className="_d-flex">
          <div className="_d-flex _justify-content-center _align-items-center">
            <img src={NoCertificateMobileMembership} className="_mr-5" alt="" />
            <p className="_text-2 _text-gray-600 _font-weight-bold _m-0">
              <I18n label="config_manager.config_certicate.access_plataform_by_desktop" />
            </p>
          </div>
        </div>

        <button
          className="hot-button hot-button--secondary hot-button--block _mt-5 _mb-0"
          onClick={this.openCertificate}
        >
          <I18n label="config_manager.config_certicate.preview_certificate" />
        </button>
      </div>
    );
  }

  renderCertificateEditor() {
    const { hasSeason, certificates } = this.props.storeCertificate;
    const { selectedCertificate } = this.state;

    return (
      <Fragment>
        <span>
          <p className="_mt-0 _mb-6 _text-2 _text-gray-600">
            <I18n label="config_manager.config_certicate.customize_certificate.description" />
          </p>
        </span>

        {hasSeason && certificates && this.renderMultipleCertificatesHeader()}

        <ConfigCertificateEditor
          openCertificate={this.openCertificate}
          certificate={selectedCertificate}
        />
      </Fragment>
    );
  }

  renderMultipleCertificatesHeader() {
    const { selectedCertificate } = this.state;
    const { allowProgressDisplay, certificates } = this.props.storeCertificate;

    return (
      <div className="certificate__multi">
        <label className="custom__options">
          <I18n label="config_manager.config_certicate.multi_certificates.new.title" />
          <InfoTooltip text="config_manager.config_certicate.multi_certificates.new.tooltip" />
        </label>
        <div className="certificate__new">
          <InputGroup
            name="newCertificate"
            maxLength="100"
            autoComplete="off"
            placeholder="config_manager.config_certicate.multi_certificates.new.name"
            disabled={!allowProgressDisplay}
            ref={this.newCertificateInput}
          />
          <Button
            size="large"
            outline={true}
            type="primary"
            onClick={this.addCertificate}
            disabled={!allowProgressDisplay}
          >
            <I18n label="config_manager.config_certicate.multi_certificates.new.add" />
          </Button>
        </div>

        <div className="certificate__select">
          <label className="custom__options">
            <I18n label="config_manager.config_certicate.multi_certificates.edit.title" />
          </label>
          <Select
            name="language"
            value={selectedCertificate}
            isClearable={false}
            options={certificates}
            onChange={this.setTemplate}
            isDisabled={!allowProgressDisplay}
          />
        </div>
      </div>
    );
  }

  render() {
    const { isMobile, storeCertificate } = this.props;
    const { allowProgressDisplay, hasChangeOnTemplate } = storeCertificate;
    const { showLoader } = this.state;

    if (showLoader) {
      return <Loading center />;
    }

    return (
      <Form
        onSubmit={this.saveHandler}
        className="form__certificate _pb-0 _pb-sm-7"
      >
        {this.renderConfigurationForm()}
        <div
          className={classNames('configcertificate _mt-9', {
            '-disabled': !allowProgressDisplay
          })}
        >
          <h2 className="_font-weight-light _text-gray-900 _text-sm-gray-800 _text-4 _mb-5 _mb-sm-2">
            <I18n label="config_manager.config_certicate.customize_certificate.title" />
          </h2>

          {isMobile
            ? this.renderMobilePreview()
            : this.renderCertificateEditor()}
        </div>

        {hasChangeOnTemplate && (
          <Alert type="info" className="_d-flex _align-items-center">
            <FontAwesomeIcon
              icon={['fal', 'exclamation-circle']}
              className="_mr-2"
            />
            <I18n label="config_manager.config_certicate.change_template" />
          </Alert>
        )}

        <ContentBasePage.Footer onlySave />
      </Form>
    );
  }
}
